import React, { FunctionComponent, useRef } from 'react';
import { joinStrings } from '../../utils/string';
import './HeroBanner.scss';
import { useNavigate } from 'react-router-dom';

export type HeroBannerProps = {
  /**
   * Path to background image
   */
  imgPath: string;
  /**
   * Alt tag to be used for background image
   */
  imgAlt: string;
  /**
   * Optional.
   * Text to overlay background image.
   * Background gradient will also be rendered when titleText is not undefined
   */
  titleText?: string;
  /**
   * Optional custom className.
   */
  className?: string;
  children?: React.ReactNode;
  link?: string;
};

/**
 * Simple Hero Banner component.
 *
 * ### Example Usage
 *
 * ```tsx
 * <HeroBanner
 *  imgPath='https://www.primeapi.com/cdn/gameRes/rect/500/Starburst.jpg'
 *  imgAlt='Starburst Slot Game'
 *  titleText='Starburst'
 * />
 * ```
 */
export const HeroBanner: FunctionComponent<HeroBannerProps> = ({
  titleText,
  imgPath,
  imgAlt,
  className,
  children,
  link,
}) => {
  const ref = useRef(null);
  // const isVisible = useElementOnScreen(ref); Left for future optimization
  const navigate = useNavigate();

  return (
    <div
      className={joinStrings(['hero-banner', className])}
      role="banner"
      aria-label={imgAlt}
      style={{ backgroundImage: `url("${imgPath}")` }}
      ref={ref}
      onClick={(): void => {
        link && navigate(link);
      }}
    >
      {titleText && (
        <div className="hero-banner__content-container">
          <div className="hero-banner__title">{titleText}</div>
        </div>
      )}
      {children}
    </div>
  );
};

HeroBanner.displayName = 'HeroBanner';
